import React from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { MapPin, Phone, Mail, Globe, MessageCircle, ArrowRight, GraduationCap, Users, ShieldCheck } from "lucide-react";
import CentreConciergeBot from "./CentreConciergeBot";

const SERVICES = [
  "Math Tutoring",
  "Science Tutoring",
  "Homework Help",
  "Test & Exam Preparation",
  "Alberta Curriculum Support",
  "Personalized Learning Plans",
  "Progress Tracking",
  "Parent Progress Reports",
];

// Neighbourhoods we serve (natural, not keyword-stuffed).
const NEIGHBOURHOODS = [
  "Bowness", "Montgomery", "Varsity", "Silver Springs",
  "Charleswood", "Panorama Hills", "Hawkwood", "Ranchlands",
  "Dalhousie", "Brentwood", "Arbour Lake", "Tuscany",
];

const FAQS = [
  {
    q: "Where is Teacher4Me Learning Centre located?",
    a: "We're at Unit 201A, 4716 16 Ave NW in Calgary, Alberta — easy access from Bowness, Montgomery, Varsity, Silver Springs, and neighbouring NW communities.",
  },
  {
    q: "What subjects do you tutor?",
    a: "Math and Science for Kindergarten through Grade 12, aligned with the Alberta curriculum. We also help with homework, exam prep and personalised learning plans.",
  },
  {
    q: "Is the first assessment really free?",
    a: "Yes. The academic assessment is completely free and comes with no obligation. Book online at /free-assessment or WhatsApp us at 825-365-8805.",
  },
  {
    q: "Do you tutor in person, online, or both?",
    a: "Both. Students attend in-person sessions at our NW Calgary location, and can continue learning online through their Teacher4Me account with progress tracked in one place.",
  },
  {
    q: "How do parents see progress?",
    a: "After every session the teacher records the topic, work completed, strengths, and areas needing improvement. Parents see it inside their Teacher4Me account, plus a weekly summary.",
  },
];

export default function CalgaryNw() {
  useSeo({
    title: "Math & Science Tutoring NW Calgary — Bowness, Varsity, Montgomery | Teacher4Me",
    description:
      "In-person Math and Science tutoring in NW Calgary — serving Bowness, Montgomery, Varsity, Silver Springs and nearby. Alberta curriculum support, homework help, exam prep. Book a free assessment.",
    canonical: "https://teacher4me.ca/calgary-nw",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": ["LocalBusiness", "EducationalOrganization"],
          "@id": "https://teacher4me.ca/calgary-nw#business",
          "name": "Teacher4Me Learning Centre",
          "alternateName": "Teacher4Me NW Calgary",
          "url": "https://teacher4me.ca/calgary-nw",
          "image": "https://teacher4me.ca/og-cover.jpg",
          "telephone": "+1-825-365-8805",
          "email": "teachmeonlineca@gmail.com",
          "priceRange": "$$",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Unit 201A, 4716 16 Ave NW",
            "addressLocality": "Calgary",
            "addressRegion": "AB",
            "postalCode": "T3B 0M4",
            "addressCountry": "CA",
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 51.083,
            "longitude": -114.163,
          },
          "areaServed": [
            {"@type": "Place", "name": "NW Calgary"},
            ...NEIGHBOURHOODS.map((n) => ({"@type": "Place", "name": `${n}, Calgary`})),
          ],
          "openingHoursSpecification": [
            {"@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "15:00", "closes": "20:00"},
            {"@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Saturday"], "opens": "10:00", "closes": "16:00"},
          ],
          "sameAs": ["https://teacher4me.ca"],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Tutoring services",
            "itemListElement": SERVICES.map((s) => ({
              "@type": "Offer",
              "itemOffered": {"@type": "Service", "name": s},
            })),
          },
        },
        {
          "@type": "FAQPage",
          "@id": "https://teacher4me.ca/calgary-nw#faq",
          "mainEntity": FAQS.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": {"@type": "Answer", "text": f.a},
          })),
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Teacher4Me", "item": "https://teacher4me.ca"},
            {"@type": "ListItem", "position": 2, "name": "Learning Centre — NW Calgary", "item": "https://teacher4me.ca/calgary-nw"},
          ],
        },
      ],
    },
  });

  const mapEmbed =
    "https://www.google.com/maps?q=" +
    encodeURIComponent("4716 16 Ave NW, Calgary, AB") + "&output=embed";

  return (
    <div>
      {/* HERO */}
      <section className="bg-paper border-b border-[#E5E7EB]">
        <div className="max-w-6xl mx-auto px-6 py-14 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="label-eyebrow flex items-center gap-2">
              <MapPin className="w-3 h-3 text-[#D92D20]" /> NW Calgary · in-person + online
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl mt-3 leading-tight tracking-tight">
              Math & Science tutoring in <span className="text-[#D92D20]">NW Calgary</span>.
            </h1>
            <p className="mt-5 text-[#4B5563] leading-relaxed">
              Personalised academic support for K-12 students across NW Calgary — Bowness,
              Montgomery, Varsity, Silver Springs, Charleswood and neighbouring communities.
              Real teachers, Alberta curriculum, progress you can see.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/free-assessment" data-testid="calgary-cta-assessment" className="btn-primary">
                BOOK FREE ASSESSMENT <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <a href="tel:8253658805" data-testid="calgary-cta-call" className="btn-outline flex items-center gap-2">
                <Phone className="w-4 h-4" /> Call
              </a>
              <a href="https://wa.me/18253658805" target="_blank" rel="noreferrer"
                data-testid="calgary-cta-whatsapp"
                className="btn-outline flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-[#064E3B]" /> WhatsApp
              </a>
            </div>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden">
            <iframe title="Teacher4Me Learning Centre map"
              src={mapEmbed} width="100%" height="280"
              style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              data-testid="calgary-map" />
            <div className="p-5 space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#D92D20] mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">Teacher4Me Learning Centre</div>
                  <div className="text-[#4B5563]">Unit 201A, 4716 16 Ave NW<br />Calgary, Alberta</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#064E3B]" />
                <a href="tel:8253658805" className="font-semibold">825-365-8805</a>
                <span className="text-[#4B5563]">· WhatsApp</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#064E3B]" />
                <a href="mailto:teachmeonlineca@gmail.com" className="font-semibold break-all">
                  teachmeonlineca@gmail.com
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-[#064E3B]" /> Teacher4Me.ca
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="label-eyebrow">What we teach</div>
        <h2 className="font-display font-black text-3xl mt-1 mb-6">Personalised, Alberta-focused.</h2>
        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {SERVICES.map((s) => (
            <li key={s} className="bg-white border border-[#E5E7EB] rounded-xl px-4 py-3 text-sm font-semibold flex items-center gap-2"
                data-testid={`calgary-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <span className="w-2 h-2 rounded-full bg-[#D92D20]" /> {s}
            </li>
          ))}
        </ul>
      </section>

      {/* WHY */}
      <section className="bg-white border-y border-[#E5E7EB]">
        <div className="max-w-6xl mx-auto px-6 py-14 grid sm:grid-cols-2 md:grid-cols-4 gap-6">
          <WhyCard icon={GraduationCap} title="Personalised Learning"
            body="Learning built around your child's grade, strengths and areas needing improvement." />
          <WhyCard icon={Users} title="Math & Science Support"
            body="Academic support aligned with Canadian and Alberta curriculum." />
          <WhyCard icon={ShieldCheck} title="Progress You Can See"
            body="Parents see what their child studied and areas still requiring work." />
          <WhyCard icon={Globe} title="Online + In-Person"
            body="Students can continue learning through Teacher4Me outside the centre." />
        </div>
      </section>

      {/* NEIGHBOURHOODS SERVED */}
      <section className="max-w-6xl mx-auto px-6 py-14" data-testid="calgary-neighbourhoods">
        <div className="label-eyebrow">NW Calgary neighbourhoods we serve</div>
        <h2 className="font-display font-black text-2xl md:text-3xl mt-1 mb-4">
          Close to home — wherever home is in NW Calgary.
        </h2>
        <p className="text-sm text-[#4B5563] max-w-3xl">
          Our centre at Unit 201A, 4716 16 Ave NW is a short drive from most NW Calgary
          neighbourhoods. Families join us from:
        </p>
        <ul className="mt-5 flex flex-wrap gap-2">
          {NEIGHBOURHOODS.map((n) => (
            <li key={n}
              data-testid={`neighbourhood-${n.toLowerCase().replace(/\s+/g, "-")}`}
              className="px-3 py-1.5 rounded-full bg-white border border-[#E5E7EB] text-xs font-semibold text-[#111827]">
              {n}
            </li>
          ))}
        </ul>
      </section>

      {/* FAQ */}
      <section className="bg-[#F9FAFB] border-y border-[#E5E7EB]" data-testid="calgary-faq">
        <div className="max-w-4xl mx-auto px-6 py-14">
          <div className="label-eyebrow">FAQ</div>
          <h2 className="font-display font-black text-2xl md:text-3xl mt-1 mb-6">
            Answers for parents.
          </h2>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <details key={f.q}
                data-testid={`faq-${i}`}
                className="bg-white border border-[#E5E7EB] rounded-lg p-4 group">
                <summary className="font-semibold text-sm cursor-pointer list-none flex items-center justify-between gap-3">
                  <span>{f.q}</span>
                  <span className="text-[#D92D20] group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm text-[#4B5563] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 py-16 text-center">
        <div className="label-eyebrow inline-flex items-center gap-2">
          <MapPin className="w-3 h-3 text-[#D92D20]" /> Now enrolling
        </div>
        <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight mt-2 max-w-3xl mx-auto leading-tight">
          Book a free assessment for your child.
        </h2>
        <p className="text-[#4B5563] mt-4">
          No obligation. Usually a same-week response.
        </p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to="/free-assessment" data-testid="calgary-cta-assessment-2" className="btn-primary">
            BOOK FREE ASSESSMENT
          </Link>
          <a href="https://wa.me/18253658805" target="_blank" rel="noreferrer"
            className="btn-outline flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-[#064E3B]" /> WhatsApp
          </a>
        </div>
      </section>

      <CentreConciergeBot />
    </div>
  );
}

function WhyCard({ icon: Icon, title, body }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
      <Icon className="w-6 h-6 text-[#D92D20]" />
      <div className="font-display font-bold text-lg mt-3">{title}</div>
      <p className="text-sm text-[#4B5563] mt-1">{body}</p>
    </div>
  );
}
